import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Search, Smartphone } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import * as api from "@/services/api";

const fmt = (d?: string | null) =>
	d ? new Date(d).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";

/**
 * Every employee's bound phone in one organization.
 *
 * Everyone on the mobile app is tied to one phone, and a phone to one person.
 * A binding can change by itself 30 days after it was made; before that only
 * FlowMetrics can release it — on a verified request, with a reason that is
 * written to the activity log.
 */
export function BoundPhonesCard({ orgId }: { orgId: string }) {
	const queryClient = useQueryClient();
	const [search, setSearch] = useState("");
	const [target, setTarget] = useState<api.BoundPhone | null>(null);
	const [reason, setReason] = useState("");

	const { data, isLoading, isError } = useQuery({
		queryKey: ["admin-bound-phones", orgId],
		queryFn: () => api.getBoundPhones(orgId),
	});

	const release = useMutation({
		mutationFn: (p: api.BoundPhone) =>
			api.releaseMobileDevice(orgId, p.userId, reason.trim()),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin-bound-phones", orgId] });
			queryClient.invalidateQueries({ queryKey: ["admin-gps-seat-holders", orgId] });
			setTarget(null);
			setReason("");
		},
	});

	const rows = useMemo(() => {
		const q = search.trim().toLowerCase();
		return (data ?? []).filter(
			(p) =>
				!q ||
				[p.name, p.email, p.employeeId, p.deviceModel]
					.filter(Boolean)
					.some((v) => String(v).toLowerCase().includes(q)),
		);
	}, [data, search]);
	const now = Date.now();

	return (
		<Card className="lg:col-span-2">
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<Smartphone className="w-5 h-5" />
					Bound phones ({data?.length ?? 0})
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				<p className="text-sm text-gray-600">
					Each employee can use only their own phone, and a phone serves only one
					employee. A binding may change by itself 30 days after it was made;
					release one earlier only on a verified request.
				</p>
				<div className="relative max-w-xs">
					<Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
					<Input
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Search employee or phone"
						className="pl-8"
					/>
				</div>

				{isLoading ? (
					<Loader2 className="w-5 h-5 animate-spin text-gray-400" />
				) : isError ? (
					<p className="text-sm text-red-600">Could not load bound phones.</p>
				) : rows.length === 0 ? (
					<p className="text-sm text-gray-500">No bound phones.</p>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="text-left text-xs text-gray-500 border-b">
									<th className="py-2 pr-3 font-medium">Employee</th>
									<th className="py-2 pr-3 font-medium">Phone</th>
									<th className="py-2 pr-3 font-medium">Can change</th>
									<th className="py-2 font-medium text-right">Release</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((p) => {
									const locked = new Date(p.changeableFrom).getTime() > now;
									return (
										<tr key={p.userId} className="border-b last:border-0">
											<td className="py-2 pr-3">
												<div className="font-medium text-gray-900">
													{p.name || p.email || p.userId}
												</div>
												<div className="text-xs text-gray-500">
													{[p.employeeId, p.email].filter(Boolean).join(" · ")}
												</div>
											</td>
											<td className="py-2 pr-3 text-xs">
												<div className="text-gray-900">
													{[p.deviceModel, p.platform].filter(Boolean).join(" · ") ||
														"Registered phone"}
												</div>
												<div className="text-gray-500">
													Bound {fmt(p.boundAt)} · last seen {fmt(p.lastSeenAt)}
												</div>
											</td>
											<td className="py-2 pr-3 text-xs">
												{locked ? (
													<span className="text-amber-700">From {fmt(p.changeableFrom)}</span>
												) : (
													<span className="text-gray-500">Now (next phone replaces it)</span>
												)}
											</td>
											<td className="py-2 text-right">
												<Button size="sm" variant="outline" onClick={() => setTarget(p)}>
													Release
												</Button>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</CardContent>

			<Dialog
				open={target !== null}
				onOpenChange={(o) => {
					if (!o) {
						setTarget(null);
						setReason("");
						release.reset();
					}
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Release phone — {target?.name}</DialogTitle>
						<DialogDescription>
							This phone stops being theirs now. The next phone they sign in on becomes their phone,
							and this phone can be used by someone else.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-1">
						<label className="block text-sm font-medium text-gray-700">
							Reason (who asked, and why)
						</label>
						<Input
							value={reason}
							onChange={(e) => setReason(e.target.value)}
							placeholder="e.g. Ticket #123 — phone lost, requested by HR"
						/>
						{release.isError && (
							<p className="text-xs text-red-600">
								{release.error instanceof Error ? release.error.message : "Release failed"}
							</p>
						)}
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setTarget(null)}>
							Cancel
						</Button>
						<Button
							variant="destructive"
							disabled={reason.trim().length < 3 || release.isPending}
							onClick={() => target && release.mutate(target)}
						>
							{release.isPending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
							Release
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</Card>
	);
}
